import { useState } from 'react';

import { Calendar } from 'cpool-components/general/Calendar';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS, type LOAN_REQUEST_OFFER_SOURCE } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuCalendar, LuLoaderCircle } from 'react-icons/lu';

import { OfferTableCell } from './OfferTableCell';
import { useOfferSentAtMutation } from '../../hooks/useOfferSentAtMutation';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { dateTimeTwoDigitFormat } from '~/lib/i18n';
import { cn } from '~/utils/cnTailwind';

type Props = {
    id: string;
    leadId: string;
    sentAt: number | null;
    source: LOAN_REQUEST_OFFER_SOURCE;
};

// Matches the "_DatePickerTrigger" design: empty trigger stays white/actionable, a recorded date greys it out.
export const SentAt: React.FC<Props> = ({ id, leadId, sentAt, source }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const queryMutator = useOfferSentAtMutation({ id, leadId, source });
    const canChangeSentAt = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST][LOAN_REQUEST_COMMANDS.changeOfferSentAt];

    if (!canChangeSentAt)
        return <OfferTableCell>{sentAt === null ? '-' : dateTimeTwoDigitFormat(new Date(sentAt))}</OfferTableCell>;

    const hasSentAt = sentAt !== null;
    const isSaving = queryMutator.isPending;

    const handleSelect = (date: Date | undefined) => {
        queryMutator.mutate(date ? date.getTime() : null);
        // Matches the DateInput close pattern: wait a frame for the popover's own click handling to
        // settle before closing it, otherwise the outside-click detection can re-open it.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setOpen(false);
            });
        });
    };

    return (
        <OfferTableCell onClick={(event) => event.stopPropagation()}>
            <Calendar
                defaultMonth={hasSentAt ? new Date(sentAt) : undefined}
                mode="single"
                onOpenChange={setOpen}
                onSelect={handleSelect}
                open={open}
                popover
                selected={hasSentAt ? new Date(sentAt) : undefined}
                trigger={
                    <button
                        className={cn(
                            'flex h-8 w-[165px] items-center gap-2 rounded border px-4 text-sm transition-colors',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            hasSentAt
                                ? 'border-grey-outline-light bg-surface-light text-midnight-placeholder'
                                : 'border-grey-stroke bg-white text-midnight hover:border-grey-outline-light',
                        )}
                        data-testid={`offer-sent-at-${id}`}
                        disabled={isSaving}
                        type="button"
                    >
                        <span className="flex-1 truncate text-left">
                            {hasSentAt
                                ? dateTimeTwoDigitFormat(new Date(sentAt))
                                : t('OFFER_MANAGEMENT.SENT_AT.PLACEHOLDER')}
                        </span>
                        <span
                            className={cn(
                                'h-4 border-l',
                                hasSentAt ? 'border-grey-outline-light' : 'border-grey-stroke',
                            )}
                        />
                        {isSaving ? (
                            <LuLoaderCircle className="shrink-0 animate-spin" size={16} />
                        ) : (
                            <LuCalendar className="shrink-0" size={16} />
                        )}
                    </button>
                }
            />
        </OfferTableCell>
    );
};
